/**
 * Parses bulk content paste: subcase headers are a full line ending with ":" (optional spaces after).
 * Every following non-blank line until the next header becomes one row with that subcase and that line as text.
 */

export type BulkContentLineJson = { subcase: string; text: string };

/** Must match server CategoryService.BULK_CREATE_CONTENT_MAX_ROWS. */
export const BULK_CREATE_CONTENT_MAX_ROWS = 500;

/** Same normalization as server bulk create (blank text → single space). */
export function normalizeBulkContentStoredText(text: string): string {
  const t = (text ?? '').trim();
  return t === '' ? ' ' : t;
}

/** Dedupe key for subcase + text (trimmed subcase; blank text stored as a single space). */
export function bulkContentDedupeKey(subcase: string, text: string): string {
  return `${subcase.trim()}\u0000${normalizeBulkContentStoredText(text)}`;
}

export function validateBulkContentLinesUniqueness(
  lines: BulkContentLineJson[],
  existingRows?: Array<{ subcase?: string | null; text?: string | null }> | null,
): string | null {
  const firstBulkRowByKey = new Map<string, number>();
  for (let i = 0; i < lines.length; i++) {
    const key = bulkContentDedupeKey(lines[i].subcase, lines[i].text);
    const first = firstBulkRowByKey.get(key);
    if (first !== undefined) {
      return `Row ${i + 1} duplicates row ${first + 1} (same subcase and text).`;
    }
    firstBulkRowByKey.set(key, i);
  }
  if (existingRows?.length) {
    const existing = new Set(
      existingRows.map((r) => bulkContentDedupeKey(r.subcase ?? '', r.text ?? '')),
    );
    for (let i = 0; i < lines.length; i++) {
      const key = bulkContentDedupeKey(lines[i].subcase, lines[i].text);
      if (existing.has(key)) {
        return `Row ${i + 1} matches an existing row in this category (same subcase and text).`;
      }
    }
  }
  return null;
}

/** Line is only "subcase:" (optional spaces after the colon, nothing else). */
const SUBCASE_HEADER = /^(.+):\s*$/;

export function parseBulkContentSubcaseBlocks(text: string): { lines: BulkContentLineJson[]; error: string | null } {
  const rawLines = text.split(/\r?\n/);
  let currentSubcase: string | null = null;
  const lines: BulkContentLineJson[] = [];

  for (let i = 0; i < rawLines.length; i++) {
    const line = rawLines[i];
    const header = SUBCASE_HEADER.exec(line);
    if (header) {
      const sc = header[1].trim();
      if (!sc) {
        return { lines: [], error: `Line ${i + 1}: empty subcase before ":".` };
      }
      currentSubcase = sc;
      continue;
    }
    if (currentSubcase === null) {
      if (line.trim() === '') {
        continue;
      }
      return {
        lines: [],
        error: `Line ${i + 1}: start with a subcase header like "Witness:" on its own line, then one row per line below it.`,
      };
    }
    const body = line.replace(/\s+$/, '');
    if (body === '') {
      continue;
    }
    lines.push({ subcase: currentSubcase, text: body });
  }

  if (lines.length === 0) {
    return {
      lines: [],
      error: 'No rows parsed. Use "MySubcase:" then one content line per row under it; repeat headers for other subcases.',
    };
  }
  if (lines.length > BULK_CREATE_CONTENT_MAX_ROWS) {
    return { lines: [], error: `At most ${BULK_CREATE_CONTENT_MAX_ROWS} rows.` };
  }
  return { lines, error: null };
}
